import { useRef, useState, type ReactNode } from 'react'

import {
  useModulePositions,
  useSetInstalledLabware,
  useTemplates,
  type ModulePosition,
  type TemplateList,
} from '@ctcells/api'
import type { ModuleSnapshot } from '@ctcells/contract'
import { errorMessage, t } from '@ctcells/i18n'

import { Button } from '../atoms/Button.tsx'
import { Field } from '../atoms/Field.tsx'
import { Modal } from '../atoms/Modal.tsx'
import { useSubmitValidation } from '../atoms/useSubmitValidation.ts'
import { TextInput } from '../atoms/TextInput.tsx'
import { ValueRows } from '../atoms/ValueRows.tsx'
import type { ActionGate } from '../screens/access.ts'
import {
  definitionDisplayName,
  draftHasUri,
  installedDraftChanged,
  installedDraftOf,
  installedLabwareItems,
  invalidEdits,
  partialRows,
  templateOptions,
  type InstalledDraft,
  type Lbw09Target,
  type WellEdit,
} from './installedLabwareModel.ts'

/**
 * 점검 줄 `RCP-LBW-09`(폐기통 설치 정의 없음)의 조치 — [설치 정의 넣기] · 창(팀장 확정 2026-10-23).
 *
 * ```
 * ┌ 설치 정의 넣기 — staining.waste_container ─────────────────┐
 * │ 모듈 staining-1 · 점검이 찾는 정의 ctcells/…_trash/1           │
 * │ 지금 설치된 것   없음                                           │
 * │ 랩웨어 템플릿    염색기 폐기통 ctcells/…/1 · 점검이 찾는 정의 [고르기] │
 * │   원점 미티칭 자리는 분주점 오프셋을 적어야 …                    │
 * │   웰 A1 x[  ] y[  ] z 0 · A2 x[  ] y[  ] z 0                   │
 * │ 사유 [________]                        [취소] [설치 정의 저장] │
 * └────────────────────────────────────────────────────────────┘
 * ```
 *
 * - 보내면 그 자리 설치 정의 PUT 하나 → 받았으면 사전 점검만 다시(작업 줄의 다른 조치와 같은 `run`).
 *   🔴 이 작업을 끊지 않는다 — 직접 입력 가드(`batch_open`)는 시작 요청(queued) 때 적는 `batch_plan` 으로 보므로 created 작업은
 *   막지 않는다(실측 2026-10-23 · 3a5b3d9f 가 created 인 채 200 · BR-137 철회). 서버가 다른 까닭으로 409 를 주면 그 원문을 적는다
 * - 🔴 **통째로 바꾸는 쓰기다** — 받은 목록 위에 고친 것만 얹는다(`installedLabwareModel.ts`). 받은 목록을 못 읽었으면 보내지 않는다
 * - 고를 랩웨어는 서버 템플릿 목록(`GET /templates/labware`)뿐이다 — 정의를 지어내지 않는다. 0건이면 빈 상태와 사유
 * - 분주점 오프셋 — 이미 있는 항목은 **받은 맵에 있는 웰만**, 새 항목은 **템플릿 정의의 웰**마다 칸을 연다(비어서 · 0 을 미리 채우지 않는다).
 *   z 는 서버가 0 만 받는다 — 보이기만
 */
export interface InstallLabwareFixProps {
  target: Lbw09Target
  module: ModuleSnapshot | undefined
  /** 권한 · 제어락 · 끊김 · 창의 다른 조치 진행 — 작업 줄 창이 짓는다 */
  writeGate: ActionGate
  /** 보낼 것을 넘긴다 — 작업 줄 창이 그 줄에 진행 · 결과를 찍고 받았으면 점검을 다시 돈다 */
  onRun: (send: () => Promise<unknown>) => void
}

export function InstallLabwareFix({ target, module, writeGate, onRun }: InstallLabwareFixProps) {
  const [open, setOpen] = useState(false)
  const positions = useModulePositions({
    moduleId: module?.module_id ?? '',
    version: module?.version,
    skip: !open || module === undefined,
  })
  const templates = useTemplates({ kind: 'labware', skip: !open })
  const install = useSetInstalledLabware()

  const buttonGate: ActionGate =
    module === undefined
      ? { disabled: true, disabledReason: t('pfFix.moduleUnknown') }
      : install.isPending
        ? { disabled: true, disabledReason: t('reason.requestInFlight') }
        : writeGate

  const position = positions.data?.items.find((item) => item.id === target.positionId)

  return (
    <>
      <Button onClick={() => setOpen(true)} size="pill" {...buttonGate}>
        {t('pfFix.installLabware')}
      </Button>
      {!open || module === undefined ? null : (
        <InstalledLabwareDialog
          moduleId={module.module_id}
          onClose={() => setOpen(false)}
          onSubmit={({ reason, draft, position: seen }) => {
            setOpen(false)
            onRun(() =>
              install.mutateAsync({
                moduleId: module.module_id,
                positionId: target.positionId,
                fields: {
                  labware_ids: installedLabwareItems(draft),
                  reason,
                  ...(typeof seen.version === 'number' ? { expected_version: seen.version } : {}),
                },
              }),
            )
          }}
          position={position}
          positionsLoading={positions.isPending}
          {...(positions.isError ? { positionsNotice: errorMessage(positions.error) } : {})}
          target={target}
          templates={templates.data}
          templatesLoading={templates.isPending}
          {...(templates.isError ? { templatesNotice: errorMessage(templates.error) } : {})}
          writeGate={buttonGate}
        />
      )}
    </>
  )
}

export interface InstalledLabwareDialogProps {
  moduleId: string
  target: Lbw09Target
  /** 모듈 위치 목록의 그 자리 — 없으면 못 읽었거나 그 모듈에 그 자리가 없다 */
  position: ModulePosition | undefined
  positionsLoading: boolean
  positionsNotice?: string | undefined
  templates: TemplateList | undefined
  templatesLoading: boolean
  templatesNotice?: string | undefined
  /** 보내기 게이트(권한 · 제어락 · 끊김 · 진행 중) */
  writeGate: ActionGate
  onClose: () => void
  onSubmit: (input: { reason: string; draft: InstalledDraft; position: ModulePosition }) => void
}

export function InstalledLabwareDialog(props: InstalledLabwareDialogProps) {
  const { position } = props
  /* 받은 목록이 오면 그 위에서 초안을 짓는다 — 자리 버전이 바뀌면(다시 받음) 새로 짓도록 열쇠로 가른다 */
  return position === undefined ? (
    <DialogShell {...props} draftPart={null} />
  ) : (
    <DialogBody key={`${position.id}|${String(position.version)}`} {...props} position={position} />
  )
}

type EditAt = (uri: string, well: string, axis: 'x' | 'y', value: string) => void

function DialogBody(props: InstalledLabwareDialogProps & { position: ModulePosition }) {
  const [draft, setDraft] = useState<InstalledDraft>(() =>
    installedDraftOf(props.position.installed_labware),
  )
  const root = useRef<HTMLDivElement>(null)
  const validation = useSubmitValidation<InstalledDraft, string>((value) => {
    const errors: Record<string, string> = {}
    const partial = new Set(partialRows(value))
    for (const row of [...value.kept.filter((row) => !row.removed), ...value.added]) {
      const wells = new Set([...row.wells, ...Object.keys(row.edits)])
      for (const well of wells)
        for (const axis of ['x', 'y'] as const) {
          const text = row.edits[well]?.[axis]
          const invalid =
            text !== undefined && (text.trim() === '' || !Number.isFinite(Number(text)))
          const missing =
            partial.has(row.definitionUri) && (text === undefined || text.trim() === '')
          if (invalid || missing)
            errors[`${row.definitionUri}|${well}|${axis}`] = missing
              ? t('validation.required')
              : t('pfFix.lbw.badNumber', { wells: `${well}.${axis}` })
        }
    }
    return errors
  })
  const fieldAt = (uri: string) => (well: string, axis: 'x' | 'y') =>
    validation.field(`${uri}|${well}|${axis}`)
  const options = templateOptions(props.templates)
  const definitionOf = (uri: string): unknown =>
    props.position.installed_labware?.find((entry) => entry.definitionUri === uri)

  const editAt: EditAt = (uri, well, axis, value) => {
    const now = draft
    const edit = (edits: Readonly<Record<string, WellEdit>>) => ({
      ...edits,
      [well]: { ...edits[well], [axis]: value },
    })
    const next = {
      kept: now.kept.map((row) =>
        row.definitionUri === uri ? { ...row, edits: edit(row.edits) } : row,
      ),
      added: now.added.map((row) =>
        row.definitionUri === uri ? { ...row, edits: edit(row.edits) } : row,
      ),
    }
    validation.change(`${uri}|${well}|${axis}`, next, Object.keys(validation.errors))
    setDraft(next)
  }

  const part = (
    <div ref={root}>
      <section aria-label={t('pfFix.lbw.installed')} className="lbwfix__sec">
        <h4 className="lbwfix__h">{t('pfFix.lbw.installed')}</h4>
        {draft.kept.length === 0 && draft.added.length === 0 ? (
          <span className="muted">{t('pfFix.lbw.none')}</span>
        ) : (
          <ul className="plainlist lbwfix__list">
            {draft.kept.map((row) => (
              <li key={row.definitionUri}>
                <span className={row.removed ? 'lbwfix__gone' : undefined}>
                  {definitionDisplayName(definitionOf(row.definitionUri)) ?? ''}{' '}
                  <span className="mono">{row.definitionUri}</span>
                </span>
                <Button
                  onClick={() =>
                    setDraft((now) => ({
                      ...now,
                      kept: now.kept.map((one) =>
                        one.definitionUri === row.definitionUri
                          ? { ...one, removed: !one.removed }
                          : one,
                      ),
                    }))
                  }
                  size="pill"
                >
                  {row.removed ? t('pfFix.lbw.keep') : t('pfFix.lbw.remove')}
                </Button>
                {row.removed ? null : row.offsets === null ? (
                  /* 받은 오프셋이 없다 — 받은 정의의 웰마다 빈 칸(새 항목과 같은 규칙) */
                  <BlankOffsets
                    fieldAt={fieldAt(row.definitionUri)}
                    onEdit={(well, axis, value) => editAt(row.definitionUri, well, axis, value)}
                    row={row}
                  />
                ) : (
                  <OffsetTable
                    fieldAt={fieldAt(row.definitionUri)}
                    edits={row.edits}
                    onEdit={(well, axis, value) => editAt(row.definitionUri, well, axis, value)}
                    wells={Object.entries(row.offsets).map(([well, vector]) => ({
                      well,
                      x: String(vector.x),
                      y: String(vector.y),
                      z: String(vector.z),
                    }))}
                  />
                )}
              </li>
            ))}
            {draft.added.map((row) => (
              <li key={row.definitionUri}>
                <span className="mono">{row.definitionUri}</span>
                <span className="muted">{t('pfFix.lbw.new')}</span>
                <Button
                  onClick={() =>
                    setDraft((now) => ({
                      ...now,
                      added: now.added.filter((one) => one.labwareId !== row.labwareId),
                    }))
                  }
                  size="pill"
                >
                  {t('pfFix.lbw.remove')}
                </Button>
                <BlankOffsets
                  fieldAt={fieldAt(row.definitionUri)}
                  onEdit={(well, axis, value) => editAt(row.definitionUri, well, axis, value)}
                  row={row}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-label={t('pfFix.lbw.templates')} className="lbwfix__sec">
        <h4 className="lbwfix__h">{t('pfFix.lbw.templates')}</h4>
        {props.templatesLoading ? (
          t('state.loading')
        ) : props.templatesNotice !== undefined ? (
          /* 🔴 「못 읽었다」를 「0건」으로 접지 않는다 */
          <span className="deny">{props.templatesNotice}</span>
        ) : options.length === 0 ? (
          <span className="muted">{t('pfFix.lbw.noTemplates')}</span>
        ) : (
          <ul className="plainlist lbwfix__list">
            {options.map((option) => (
              <li key={option.labwareId}>
                <span>
                  {option.label} <span className="mono">{option.definitionUri}</span>
                </span>
                {option.definitionUri === props.target.definitionUri ? (
                  <span className="lbwfix__wanted">{t('pfFix.lbw.wanted')}</span>
                ) : null}
                <Button
                  onClick={() =>
                    setDraft((now) => ({
                      ...now,
                      added: [
                        ...now.added,
                        {
                          labwareId: option.labwareId,
                          definitionUri: option.definitionUri,
                          wells: option.wells,
                          edits: {},
                        },
                      ],
                    }))
                  }
                  size="pill"
                  {...(draftHasUri(draft, option.definitionUri)
                    ? ({
                        disabled: true,
                        disabledReason: t('pfFix.lbw.alreadyListed'),
                      } as const)
                    : {})}
                >
                  {t('pfFix.lbw.add')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )

  const draftGate: ActionGate | undefined =
    invalidEdits(draft).length === 0 &&
    partialRows(draft).length === 0 &&
    !installedDraftChanged(draft)
      ? { disabled: true, disabledReason: t('pfFix.lbw.noChange') }
      : undefined

  return (
    <DialogShell
      {...props}
      draftGate={draftGate}
      validateDraft={() => validation.validate(draft, root.current)}
      draftPart={part}
      onSend={(reason) => props.onSubmit({ reason, draft, position: props.position })}
    />
  )
}

/** 빈 칸으로 여는 오프셋 — 서버 규칙 한 줄 + 정의 웰마다 x · y(🔴 0 을 미리 채우지 않는다) */
type OffsetField = ReturnType<
  ReturnType<typeof useSubmitValidation<InstalledDraft, string>>['field']
>

function BlankOffsets({
  fieldAt,
  row,
  onEdit,
}: {
  fieldAt: (well: string, axis: 'x' | 'y') => OffsetField
  row: { wells: readonly string[]; edits: Readonly<Record<string, WellEdit>> }
  onEdit: (well: string, axis: 'x' | 'y', value: string) => void
}) {
  if (row.wells.length === 0) return null
  return (
    <>
      <p className="small muted lbwfix__rule">{t('pfFix.lbw.offsetRule')}</p>
      <OffsetTable
        fieldAt={fieldAt}
        edits={row.edits}
        onEdit={onEdit}
        wells={row.wells.map((well) => ({ well, x: '', y: '', z: '0' }))}
      />
    </>
  )
}

/** 웰마다 x · y 입력 · z 는 보이기만(서버가 0 만 받는다) */
function OffsetTable({
  fieldAt,
  wells,
  edits,
  onEdit,
}: {
  fieldAt: (well: string, axis: 'x' | 'y') => OffsetField
  /** 웰 · 처음 보일 값(받은 값 또는 빈 칸) */
  wells: readonly { well: string; x: string; y: string; z: string }[]
  edits: Readonly<Record<string, WellEdit>>
  onEdit: (well: string, axis: 'x' | 'y', value: string) => void
}) {
  return (
    <table className="lbwfix__offsets">
      <caption className="small muted">{t('pfFix.lbw.offsets')}</caption>
      <thead>
        <tr>
          <th scope="col">{t('pfFix.lbw.well')}</th>
          <th scope="col">x</th>
          <th scope="col">y</th>
          <th scope="col">z</th>
        </tr>
      </thead>
      <tbody>
        {wells.map((cell) => (
          <tr key={cell.well}>
            <th className="mono" scope="row">
              {cell.well}
            </th>
            {(['x', 'y'] as const).map((axis) => (
              <td key={axis}>
                <Field label={`${cell.well} ${axis}`} {...fieldAt(cell.well, axis).fieldProps}>
                  <TextInput
                    {...fieldAt(cell.well, axis).inputProps}
                    aria-label={`${cell.well} ${axis}`}
                    inputMode="decimal"
                    onChange={(value) => onEdit(cell.well, axis, value)}
                    value={edits[cell.well]?.[axis] ?? cell[axis]}
                  />
                </Field>
              </td>
            ))}
            <td className="mono">{cell.z}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function DialogShell({
  moduleId,
  target,
  positionsLoading,
  positionsNotice,
  writeGate,
  onClose,
  draftPart,
  draftGate,
  validateDraft,
  onSend,
}: InstalledLabwareDialogProps & {
  validateDraft?: (() => boolean) | undefined
  draftPart: ReactNode
  draftGate?: ActionGate | undefined
  onSend?: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  const validation = useSubmitValidation((value: string) => ({
    reason: value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')
  const noPosition = positionsLoading
    ? t('state.loading')
    : (positionsNotice ?? t('pfFix.lbw.noPosition'))
  const sendGate: ActionGate =
    onSend === undefined
      ? { disabled: true, disabledReason: noPosition }
      : writeGate.disabled
        ? writeGate
        : draftGate !== undefined
          ? draftGate
          : { disabled: false }

  return (
    <Modal
      size="md"
      footer={
        <>
          <Button onClick={onClose} size="pill">
            {t('action.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (sendGate.disabled) return
              const reasonOk = validation.validate(
                reason,
                document.getElementById(reasonField.inputProps.id)?.parentElement ?? null,
              )
              const draftOk = validateDraft?.() ?? true
              if (reasonOk && draftOk) onSend?.(reason.trim())
            }}
            size="pill"
            variant="primary"
            {...sendGate}
          >
            {t('pfFix.lbw.send')}
          </Button>
        </>
      }
      onClose={onClose}
      title={t('pfFix.lbw.title', { position: target.positionId })}
    >
      <ValueRows
        rows={[
          {
            key: 'module',
            label: t('pfFix.lbw.module'),
            value: <span className="mono">{moduleId}</span>,
          },
          {
            key: 'wanted',
            label: t('pfFix.lbw.wantedRow'),
            value:
              target.definitionUri === undefined ? (
                <span className="muted">{t('pfFix.lbw.none')}</span>
              ) : (
                <span className="mono">{target.definitionUri}</span>
              ),
          },
        ]}
      />
      {draftPart === null ? (
        positionsLoading ? (
          <p className="muted">{t('state.loading')}</p>
        ) : (
          <p className="deny" role="alert">
            {noPosition}
          </p>
        )
      ) : (
        draftPart
      )}
      <Field {...reasonField.fieldProps} label={t('field.reason')}>
        <TextInput
          {...reasonField.inputProps}
          onChange={(next) => {
            setReason(next)
            validation.change('reason', next)
          }}
          value={reason}
          wide
        />
      </Field>
    </Modal>
  )
}
